import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import { X, CheckCircle2, Paperclip, Loader2 } from "lucide-react";
import { MultiFileUpload } from "@/modules/dashboard/payments/components/MultiFileUpload";
import useTranslations from "@/shared/lang/useTranslations";
import { CONFIRMATION_SOURCE_OPTIONS } from "../constants/confirmationSourceOptions";
import { cn } from "@/lib/utils";

type ConfirmTransactionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attachments?: File[], confirmation_source?: string) => void;
  isLoading?: boolean;
};

export const ConfirmTransactionModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: ConfirmTransactionModalProps) => {
  const { t } = useTranslations("confirmations");
  const [confirmationSource, setConfirmationSource] = useState<string>("");
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [showEvidenceSection, setShowEvidenceSection] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setConfirmationSource("");
    setSourceError(null);
    setShowEvidenceSection(false);
    setAttachments([]);
    setAttachmentError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      const id = setTimeout(resetForm, 0);
      return () => clearTimeout(id);
    }
  }, [isOpen, resetForm]);

  const handleConfirmClick = useCallback(() => {
    const source = confirmationSource.trim();
    if (!source) {
      setSourceError(t("modal_confirm_source_required"));
      return;
    }
    setSourceError(null);
    if (showEvidenceSection && attachments.length === 0) {
      setAttachmentError(t("modal_confirm_evidence_required"));
      return;
    }
    setAttachmentError(null);
    onConfirm(attachments.length > 0 ? attachments : undefined, source);
  }, [showEvidenceSection, attachments, confirmationSource, onConfirm, t]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 transform transition-all">
                {isLoading && (
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 dark:bg-zinc-900/80 backdrop-blur-[2px]"
                    aria-hidden
                  >
                    <Loader2 className="h-10 w-10 shrink-0 animate-spin text-green-500 dark:text-green-400" />
                  </div>
                )}
                <div className="flex items-center justify-between px-8 pt-8 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500/10">
                      <CheckCircle2 className="h-6 w-6 text-green-500 dark:text-green-400" />
                    </div>
                    <h2 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">
                      {t("modal_confirm_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1"
                    aria-label={t("modal_confirm_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-8 py-2">
                  <p className="text-gray-600 dark:text-zinc-400 text-base leading-relaxed">
                    {t("modal_confirm_description")}
                  </p>
                </div>

                <div className="px-8 py-4 space-y-2">
                  <label
                    htmlFor="confirm-transaction-source"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t("modal_confirm_source_label")}{" "}
                    <span className="text-red-500 dark:text-red-400" aria-hidden>*</span>
                  </label>
                  <select
                    id="confirm-transaction-source"
                    value={confirmationSource}
                    onChange={(e) => {
                      setConfirmationSource(e.target.value);
                      if (sourceError) setSourceError(null);
                    }}
                    disabled={isLoading}
                    required
                    aria-required="true"
                    aria-invalid={!!sourceError}
                    aria-describedby={sourceError ? "confirm-source-error" : undefined}
                    className={cn(
                      "w-full rounded-lg border bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-base",
                      "px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      sourceError
                        ? "border-red-500 dark:border-red-400"
                        : "border-gray-300 dark:border-zinc-600"
                    )}
                  >
                    <option value="">{t("modal_confirm_source_placeholder")}</option>
                    {CONFIRMATION_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {sourceError && (
                    <p
                      id="confirm-source-error"
                      className="text-sm font-medium text-red-500 dark:text-red-400"
                      role="alert"
                    >
                      {sourceError}
                    </p>
                  )}
                </div>

                <div className="px-8 py-4 flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setShowEvidenceSection((prev) => !prev)}
                    disabled={isLoading}
                    className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50 transition-colors"
                  >
                    <Paperclip className="h-4 w-4 shrink-0" />
                    {t("modal_confirm_attach_evidence")}
                  </button>
                  {showEvidenceSection && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowEvidenceSection(false);
                        setAttachments([]);
                        setAttachmentError(null);
                      }}
                      disabled={isLoading}
                      className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 transition-colors shrink-0"
                    >
                      {t("modal_confirm_no_attachments")}
                    </button>
                  )}
                </div>

                {showEvidenceSection && (
                  <div className="px-8 py-2 pb-4">
                    <MultiFileUpload
                      value={attachments}
                      onChange={(files) => {
                        setAttachments(files);
                        if (attachmentError) setAttachmentError(null);
                      }}
                      label={t("modal_confirm_evidence_label")}
                      description={t("modal_confirm_evidence_description")}
                      instructionText={t("modal_confirm_evidence_instruction")}
                      maxSizeMB={10}
                      disabled={isLoading}
                      error={attachmentError ?? undefined}
                      inputId="confirm-transaction-evidence"
                      allowPdf={false}
                    />
                  </div>
                )}

                <div className="px-8 py-8 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="min-w-[100px] h-12 px-6 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-white text-base font-bold transition-all hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50"
                  >
                    {t("modal_confirm_back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmClick}
                    disabled={isLoading}
                    className="min-w-[180px] h-12 px-8 rounded-full bg-green-500 text-white text-base font-bold transition-all hover:brightness-110 shadow-lg shadow-green-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      t("modal_confirm_confirm")
                    )}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
